/**
 * Chart colour is resolved through CSS variables so light and dark swap at the
 * root with everything else. Recharts needs concrete strings for SVG fills, and
 * `rgb(var(--series-1))` is a valid CSS colour, so the indirection costs nothing.
 *
 * Categorical slots are assigned in FIXED ORDER and follow the entity, never its
 * rank — filtering a series out must never repaint the survivors. Never generate
 * a 7th hue: fold the tail into "Other" or facet instead.
 *
 * Validated with dataviz/scripts/validate_palette.js against this app's surfaces
 * (light #FFFDFB, dark #191512), adjacent pairlist:
 *   light  CVD PASS (worst 16.3) · normal-vision PASS (19.6) · contrast WARN
 *   dark   every check PASS
 * The light-mode WARN is why ChartFrame always ships a table view.
 */

export const SERIES = [
  "rgb(var(--series-1))",
  "rgb(var(--series-2))",
  "rgb(var(--series-3))",
  "rgb(var(--series-4))",
  "rgb(var(--series-5))",
  "rgb(var(--series-6))",
] as const;

export const MAX_SERIES = SERIES.length;

export function seriesColor(index: number): string {
  return SERIES[index % MAX_SERIES] as string;
}

/** Sequential ramp — one hue, light to dark. Magnitude only, never identity. */
export const SEQUENTIAL = [
  "rgb(var(--seq-100))",
  "rgb(var(--seq-250))",
  "rgb(var(--seq-400))",
  "rgb(var(--seq-550))",
  "rgb(var(--seq-700))",
] as const;

/**
 * Status tokens. Reserved meaning: used only where the value genuinely means
 * good/bad, never as "series 4". Always paired with an icon or a text label.
 */
export const STATUS = {
  good: "rgb(var(--good))",
  warn: "rgb(var(--warn))",
  bad: "rgb(var(--bad))",
  neutral: "rgb(var(--muted))",
  /**
   * Approved leave. Reserved like the four above and used for nothing else.
   *
   * It is deliberately not good/warn/bad: a sanctioned day away is not a
   * success, a problem, or a failure, and borrowing one of those tokens would
   * assert something the data doesn't say. The accent is the one hue already in
   * the system that separates cleanly from all three — worst adjacent ΔE 22
   * (protan) / 26 (normal-vision) against this app's surfaces, target 8 — so
   * leave never has to be told apart from absence by shade alone.
   */
  leave: "rgb(var(--accent))",
  /** Applied for, not yet decided: the same hue, unsaturated, so it reads as a
   *  provisional form of leave rather than a fifth unrelated state. */
  leavePending: "rgb(var(--accent-line))",
  /**
   * An upcoming day approved to be worked from home.
   *
   * Borrows the second categorical slot rather than taking a reserved status
   * token, and that is the deliberate choice: working from home is not good,
   * warn or bad, and it is emphatically not `leave` — a day worked wearing the
   * colour of a day off is the single worst confusion this feature could cause.
   * The categorical slot is the honest place for a state that is a KIND rather
   * than a verdict, and series-2 sits well clear of the accent leave uses.
   */
  wfh: "rgb(var(--series-2))",
} as const;

/** Emphasis form: one series in the accent, everything else recessive. */
export const EMPHASIS = {
  focus: "rgb(var(--accent))",
  rest: "rgb(var(--line-strong))",
} as const;

export const CHROME = {
  grid: "rgb(var(--grid))",
  axis: "rgb(var(--axis))",
  tick: "rgb(var(--muted))",
  surface: "rgb(var(--chart-surface))",
} as const;

/** Shared Recharts axis styling: recessive, hairline, solid — never dashed. */
export const AXIS_PROPS = {
  stroke: CHROME.axis,
  tick: { fill: CHROME.tick, fontSize: 11 },
  tickLine: false,
  axisLine: { stroke: CHROME.axis },
} as const;

export const GRID_PROPS = {
  stroke: CHROME.grid,
  strokeDasharray: "0",
  vertical: false,
} as const;

/** Mark specs from the data-viz standard. */
export const MARK = {
  /** Bars never fill their slot; the leftover band is deliberate air. */
  maxBarSize: 24,
  /** 4px rounded data-end, square at the baseline. */
  radiusUp: [4, 4, 0, 0] as [number, number, number, number],
  radiusRight: [0, 4, 4, 0] as [number, number, number, number],
  lineWidth: 2,
  dotRadius: 4,
  /** 2px gap in the surface colour separates touching marks. */
  gap: 2,
  areaOpacity: 0.1,
} as const;
